import { createFileRoute, Link } from '@tanstack/react-router'
import { contentWrapper } from '../../../../../styles/contentWrapper'
import { pageTitle, sectionWrapper, titleWrapper } from '../../../../../styles/sectionStyles'
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import { css } from '../../../../../../styled-system/css'
import { button } from '../../../../../styles/buttonStyles'
import { useEffect, useState } from 'react'
import { useDeleteStoreParameter, useGetAllCities, useGetCategories, useGetMyCompanies, useUpdateStore, useUpdateStoreImage } from '../../../../../libs/admin/hooks'
import { LoadingOverlay } from '../../../../../components/loading-overlay'
import { ValidatableTextInput, ValidatableLatLngInput } from '../../../../../components/validatable-inputs/text-input'
import { ValidatableSelect } from '../../../../../components/validatable-inputs/select'
import { generateReadableId } from '../../../../../libs/utils'
import { Image } from 'schema/gen/es/chiikipoint/model/v2/model_pb'
import { StoreImageInput } from '../../../../../components/store-image-input'
import { storeInfoSchema } from '../../-schemas/store-info'
import type { z } from 'zod'
import { set } from 'date-fns'
import { ValidatableDayPicker } from '../../../../../components/validatable-inputs/day-picker'
import { Timestamp } from '@bufbuild/protobuf'
import toast from 'react-hot-toast'

export const Route = createFileRoute('/admin/companies/$companyId/$storeId/edit')({
  component: RouteComponent,
})

const useStoreData = () => {
  const { companyId, storeId } = Route.useParams()
  const { data: myCompanies, isLoading, isError, dataUpdatedAt } = useGetMyCompanies()
  if (isLoading) {
    return { isLoading, allStoreSlugs: [] as string[] }
  }
  if (isError) {
    return { isError, allStoreSlugs: [] as string[] }
  }
  if (!companyId || !storeId) {
    throw new Error('Company ID or Store ID is missing')
  }
  if (!myCompanies) {
    throw new Error('My companies data not found')
  }
  const company = myCompanies.find((company) => company.company?.id === companyId)
  if (!company) {
    throw new Error('Company not found')
  }
  const store = company.stores.find((store) => store.id === storeId)
  if (!store) {
    throw new Error('Store not found')
  }
  const allStoreSlugs = myCompanies.flatMap((c) =>
    (c.stores ?? [])
      .filter((s) => s.id !== storeId)
      .map((s) => s.slug)
      .filter((slug): slug is string => Boolean(slug))
  )
  return {
    companyId,
    storeId,
    company,
    store,
    allStoreSlugs,
    isLoading,
    isError,
    dataUpdatedAt,
  }
}

function RouteComponent() {
  return <Layout />
}

const Layout = () => {
  const { isLoading, isError } = useStoreData()
  if (isLoading) {
    return (
      <div className={contentWrapper()}>
        <LoadingOverlay />
      </div>
    )
  }
  if (isError) {
    console.error("Error loading store data")
    return null
  }

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <div className={pageTitle()}>
          <Link to='..'>
            <ArrowBack />
          </Link>
          店舗情報編集
        </div>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <BreadCrumb />
        <StoreInfoPane />
      </div>
    </div>
  )
}

const BreadCrumb = () => {
  const { companyId, company, isLoading, isError } = useStoreData()
  if (isLoading || isError) {
    return null
  }
  if (!company || !company.company) {
    return null
  }
  return (
    <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    })}>
      <Link
        to="/admin/companies/$companyId"
        params={{ companyId }}
      >
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>
          {company.company.name}
        </span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <Link
        to=".."
      >
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>
          店舗情報
        </span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <span className={css({
        color: 'text.tertiary',
      })}>店舗情報編集</span>
    </div>
  )
}

type storeInfo = z.infer<typeof storeInfoSchema>

const StoreInfoPane = () => {
  // requests
  const { companyId, storeId, store, allStoreSlugs, isLoading: isStoreFetching, isError: isStoreError, dataUpdatedAt } = useStoreData()
  const { data: categories, isLoading: isCategoryFetching, isError: isCategoryError } = useGetCategories()
  const { data: cities, isLoading: isCityFetching, isError: isCityError } = useGetAllCities()
  const { mutateAsync: mutateAsyncStore, isPending: isPendingStore } = useUpdateStore()
  const { mutateAsync: mutateAsyncImage, isPending: isPendingImage } = useUpdateStoreImage()
  const { mutateAsync: mutateAsyncDeleteParameter, isPending: isPendingDeleteParameter } = useDeleteStoreParameter()

  const navigate = Route.useNavigate()
  const [storeInfo, setStoreInfo] = useState<storeInfo>()
  const [image, setImage] = useState<File | undefined | null>(undefined)

  useEffect(() => {
    if (store) {
      setStoreInfo({
        name: store.name,
        category: store.category,
        coordinate: store.location?.longitude && store.location?.latitude ? {
          lat: store.location?.latitude,
          lng: store.location?.longitude
        } : undefined,
        fullAddress: store.fullAddress,
        cityAddress: store.city || "",
        slug: store.slug || generateReadableId(8),
        operationalPeriod: {
          openedAt: store.openedAt ? store.openedAt.toDate() : set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
          closedAt: store.closedAt ? store.closedAt.toDate() : undefined,
        },
      })
    }
  }, [store])

  if (isStoreFetching || isCategoryFetching || isCityFetching || storeInfo === undefined) {
    return null
  }
  if (isStoreError || isCategoryError || isCityError) {
    console.error("Error loading data")
    return null
  }
  if (!store) {
    return null
  }

  // Validation
  const parseResult = storeInfoSchema.safeParse(storeInfo)
  const fieldErrors = parseResult.error?.format()

  const hasDuplicateSlug = storeInfo.slug ? allStoreSlugs.includes(storeInfo.slug) : false

  const isValid = !hasDuplicateSlug && parseResult.success

  const handleSubmit = async () => {
    if (!isValid) {
      return
    }
    const { name, category, coordinate, fullAddress, cityAddress, slug } = parseResult.data
    try {
      if (image) {
        const imageObject = new Image({
          image: new Uint8Array(await image.arrayBuffer()),
          mimeType: image.type,
        })
        await mutateAsyncImage({
          storeId: store.id,
          image: imageObject,
        })
      }
      // update
      await mutateAsyncStore({
        storeId: store.id,
        name,
        category,
        latitude: coordinate?.lat,
        longitude: coordinate?.lng,
        fullAddress,
        cityAddress,
        slug,
        openedAt: storeInfo.operationalPeriod?.openedAt ? Timestamp.fromDate(storeInfo.operationalPeriod.openedAt) : undefined,
        closedAt: storeInfo.operationalPeriod?.closedAt ? Timestamp.fromDate(storeInfo.operationalPeriod.closedAt) : undefined,
      })
      // delete
      const deletableparams = []
      if (!coordinate) {
        deletableparams.push("coordinate")
      }
      if (deletableparams.length > 0) {
        await mutateAsyncDeleteParameter({
          storeId: store.id,
          location: deletableparams.includes("coordinate"),
        })
      }
      
      navigate({
        to: "..",
        params: {
          companyId,
          storeId,
        },
      })
    } catch (error) {
      toast.error('店舗情報の更新に失敗しました。')
    }
  }

  return (
    <div className={css({
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      width: '100%',
    })}>
      <div className={sectionWrapper()}>
        <ValidatableTextInput
          title='店舗名'
          placeholder='ポケットストア'
          value={storeInfo.name ?? ''}
          onChange={(value) => {
            setStoreInfo({
              ...storeInfo,
              name: value,
            })
          }}
          isValid={!fieldErrors?.name}
          validationMessage={fieldErrors?.name?._errors[0] ?? 'OK'}
          type='string'
          isRequired
        />
        <ValidatableSelect
          title='市区町村'
          value={storeInfo.cityAddress ?? ''}
          onChange={(value) => {
            setStoreInfo({
              ...storeInfo,
              cityAddress: value,
              fullAddress: value,
            })
          }}
          isValid={!fieldErrors?.cityAddress}
          validationMessage={fieldErrors?.cityAddress?._errors[0] ?? 'OK'}
          options={['', ...(cities || [])].map((e) => ({ label: e, value: e }))}
          isRequired
        />
        <ValidatableTextInput
          title='住所'
          placeholder='○○県○○市○○町○丁目○番地○'
          value={storeInfo.fullAddress ?? ''}
          onChange={(value) => {
            setStoreInfo({
              ...storeInfo,
              fullAddress: value,
            })
          }}
          isValid={!fieldErrors?.fullAddress}
          validationMessage={fieldErrors?.fullAddress?._errors[0] ?? 'OK'}
          type='string'
        />
        <ValidatableSelect
          title='カテゴリ'
          value={storeInfo.category ?? ''}
          onChange={(value) => {
            setStoreInfo({
              ...storeInfo,
              category: value,
            })
          }}
          isValid={!fieldErrors?.category}
          validationMessage={fieldErrors?.category?._errors[0] ?? 'OK'}
          options={['', ...(categories || [])].map((e) => ({ label: e, value: e }))}
          isRequired
        />
        <ValidatableLatLngInput
          title="店舗の位置"
          value={storeInfo.coordinate}
          onChange={(value) => {
            setStoreInfo({
              ...storeInfo,
              coordinate: value,
            })
          }}
          isValid={!fieldErrors?.coordinate}
          validationMessage={fieldErrors?.coordinate?._errors[0] ?? 'OK'}
        />
        <ValidatableTextInput
          title='店舗QRコード識別子'
          description='任意の識別子を指定できます。共通QRコードとして利用する場合は、別テナントの店舗と同じ識別子を指定します。'
          placeholder='英数字8文字'
          value={storeInfo.slug ?? ''}
          onChange={(value) => {
            setStoreInfo({
              ...storeInfo,
              slug: value,
            })
          }}
          isValid={!fieldErrors?.slug && !hasDuplicateSlug}
          validationMessage={fieldErrors?.slug?._errors[0] ?? (hasDuplicateSlug ? 'このQRコード識別子はすでに使用されています' : 'OK')}
          type='string'
          isRequired
        />
        <StoreImageInput
          onFileChange={setImage}
          defaultImageUrl={`${store.iconUrl}?v=${dataUpdatedAt}`}
          shouldShowResetButton={true}
        />
        <ValidatableDayPicker
        title='利用開始日'
        description='店舗の利用開始日を指定します。'
        placeholder='yyyy/MM/dd'
        value={storeInfo.operationalPeriod?.openedAt ? new Date(storeInfo.operationalPeriod.openedAt) : undefined}
        onChange={(date) => {
          setStoreInfo({
            ...storeInfo,
            operationalPeriod: {
              ...storeInfo.operationalPeriod,
              openedAt: date ? set(date, { hours: 0, minutes: 0, seconds: 0 }) : set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
            }
          })
        }}
        isValid={!fieldErrors?.operationalPeriod}
        validationMessage={fieldErrors?.operationalPeriod?._errors[0] ?? 'OK'}
        isRequired
        format='yyyy/MM/dd'
      />
      <ValidatableDayPicker
        title='利用終了日'
        description='店舗の利用終了日を指定します。指定した日時の23:59:59まで利用可能です。'
        placeholder='yyyy/MM/dd'
        value={storeInfo.operationalPeriod?.closedAt ? new Date(storeInfo.operationalPeriod.closedAt) : undefined}
        onChange={(date) => {
          setStoreInfo({
            ...storeInfo,
            operationalPeriod: {
              ...storeInfo.operationalPeriod,
              closedAt: date ? set(date, { hours: 23, minutes: 59, seconds: 59 }) : store.closedAt?.toDate() // revert to current value
            }
          })
        }}
        isValid={!fieldErrors?.operationalPeriod}
        validationMessage={fieldErrors?.operationalPeriod?._errors[0] ?? 'OK'}
        format='yyyy/MM/dd'
      />
      </div>
      <button
        className={button({ color: 'primary' })}
        type="button"
        disabled={!isValid || isPendingStore || isPendingImage || isPendingDeleteParameter}
        onClick={handleSubmit}
      >
        登録
      </button>
    </div>
  )
}
